import React from 'react'
import {useSelector} from 'react-redux'
import {Cashback as CashbackType, SplitBookingBundle} from '@findhotel/sapi'
import {SplitBookingOfferInfo} from 'components/Offer/types'
import {useVioTag} from 'hooks/useVioTag'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getCheapestPriceRateBreakdown,
  getOfferAccessFlowSettings,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'

import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {OfferCompactLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {OfferVariant} from '@vio/core/src/offer/types/OfferVariant'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {Cashback} from '@vio/shared/src/cashback/components/Cashback'
import {OfferLayout} from '@vio/shared/src/search/OfferLayout'

import {OfferInteraction} from '../Shared/OfferInteraction'
import {ProviderLogoComponent} from '../Shared/ProviderLogoComponent'
import {SplitBookingOfferInteraction} from '../Shared/SplitBookingOfferInteraction'
import {CtaComponent} from './CtaComponent'
import {PriceComponent} from './PriceComponent'
import {RoomLabelsComponent} from './RoomLabelsComponent'
import {TagComponent} from './TagComponent'

interface MainProps {
  isLoading?: false
  variant: OfferCompactLayoutVariant
  hotelId: string
  offer: SearchOffer
  anchorPrice: number
  anchorPriceTotal?: number
  offerVariant: OfferVariant
  hasRoomLabels?: boolean
  hasTag?: boolean
  showArrowCTA?: boolean
  isSplitBooking?: false
  hotelHasMoreThanOneOffer?: boolean
  sourceComponent: string
  offerPosition: number
  hasInteraction: boolean
  splitBookingOffers?: never
  splitBookingBundle?: never
  showCashback?: boolean
  showCashbackLineBreak?: boolean
  disabled?: boolean
}

interface SplitBookingBundleWithCashback extends SplitBookingBundle {
  cashback?: CashbackType
}

interface SplitBookingProps {
  isLoading?: false
  variant: OfferCompactLayoutVariant
  hotelId: string
  hasRoomLabels?: boolean
  hasTag?: boolean
  shouldSeeOffersUnlocked?: boolean
  showArrowCTA?: boolean
  isSplitBooking: true
  splitBookingOffers?: SplitBookingOfferInfo[]
  splitBookingBundle?: SplitBookingBundleWithCashback
  hotelHasMoreThanOneOffer?: boolean
  sourceComponent?: string
  offerPosition?: never
  hasInteraction?: never
  offer?: never
  anchorPrice?: number
  anchorPriceTotal?: number
  offerVariant?: never
  showCashback?: boolean
  showCashbackLineBreak?: boolean
  disabled?: boolean
}

interface LoadingProps {
  isLoading: true
  variant: OfferCompactLayoutVariant
  hotelId?: never
  offer?: never
  anchorPrice?: never
  anchorPriceTotal?: never
  offerVariant?: never
  hasRoomLabels?: never
  hasTag?: never
  shouldSeeOffersUnlocked?: never
  showArrowCTA?: never
  isSplitBooking?: never
  splitBookingOffers?: never
  splitBookingBundle?: never
  hotelHasMoreThanOneOffer?: never
  sourceComponent?: never
  offerPosition?: never
  hasInteraction?: never
  showCashback?: never
  showCashbackLineBreak?: never
  disabled?: never
}

export const CompactOffer: React.FC<
  MainProps | SplitBookingProps | LoadingProps
> = ({
  variant,
  isLoading,
  hotelId,
  offer,
  offerVariant,
  anchorPrice,
  anchorPriceTotal = 0,
  hasRoomLabels = false,
  hasTag = true,
  showArrowCTA = false,
  isSplitBooking = false,
  splitBookingOffers,
  splitBookingBundle,
  hotelHasMoreThanOneOffer = false,
  sourceComponent,
  offerPosition,
  hasInteraction = true,
  showCashback = false,
  showCashbackLineBreak = false,
  disabled
}) => {
  const currencyCode = useSelector(getCurrencyCode)
  const cheapestPriceRateBreakdown = useSelector(state =>
    getCheapestPriceRateBreakdown(state, hotelId)
  )

  const cashbackValue =
    offer?.cashback?.value || splitBookingBundle?.cashback?.value
  const cashbackType =
    offer?.cashback?.type || splitBookingBundle?.cashback?.type

  const {isAppRequired, isSpecial} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )

  const showTotalPrices = useSelector(getShowTotalPrices)
  const {isVioTagVisible: isVioTagEligible} = useVioTag({
    price: showTotalPrices ? offer?.totalRate : offer?.nightlyRate,
    anchorPrice: showTotalPrices ? anchorPriceTotal : anchorPrice,
    currencyCode,
    offerTags: offer?.tags
  })

  // The tag is only visible for the cheapest offer in the list
  // and for offers cheaper than the cheapest offer
  const isVioTagVisible =
    isVioTagEligible &&
    offerVariant === OfferVariant.OnlyOnVio &&
    offer?.nightlyRate <= cheapestPriceRateBreakdown?.nightlyRate

  const isGreatDealTagVisible = offerVariant === OfferVariant.GreatDeal

  if (isLoading) return <OfferLayout variant={variant} isLoading />

  if (isSplitBooking) {
    return (
      <SplitBookingOfferInteraction
        component={sourceComponent}
        hotelId={hotelId}
      >
        <OfferLayout
          variant={variant}
          providerLogoComponent={
            <ProviderLogoComponent
              hotelId={hotelId}
              isSplitBooking
              splitBookingOffers={splitBookingOffers}
              variant={variant}
            />
          }
          roomLabelsComponent={
            <>
              <RoomLabelsComponent
                isSplitBooking
                hasRoomLabels={hasRoomLabels}
                splitBookingOffers={splitBookingOffers}
                hotelId={hotelId}
              />
              <Text variant="bodyXS" colorPath="content.neutral.secondary">
                <FormattedMessageWrapper
                  id="roomSwitchRequired"
                  defaultMessage="Room switch required"
                />
              </Text>
            </>
          }
          priceComponent={
            <PriceComponent
              isSplitBooking
              hotelId={hotelId}
              anchorPrice={anchorPrice}
              anchorPriceTotal={anchorPriceTotal}
            />
          }
          ctaComponent={
            <CtaComponent
              isSplitBooking={isSplitBooking}
              hotelId={hotelId}
              showArrowCTA={showArrowCTA}
              offer={offer}
              disabled={disabled}
            />
          }
          cashbackComponent={
            showCashback && cashbackType && cashbackValue ? (
              <Cashback value={cashbackValue} type={cashbackType} />
            ) : null
          }
          showCashbackLineBreak={showCashbackLineBreak}
        />
      </SplitBookingOfferInteraction>
    )
  }

  return (
    <OfferInteraction
      anchorPriceTotal={anchorPriceTotal}
      hotelId={hotelId}
      offer={offer}
      component={sourceComponent}
      offerPosition={offerPosition}
      hasInteraction={hasInteraction}
    >
      <OfferLayout
        variant={variant}
        tagComponent={
          (isVioTagVisible || isGreatDealTagVisible) && (
            <TagComponent
              offer={offer}
              anchorPrice={anchorPrice}
              anchorPriceTotal={anchorPriceTotal}
              offerVariant={offerVariant}
              hotelHasMoreThanOneOffer={hotelHasMoreThanOneOffer}
              hasTag={hasTag}
            />
          )
        }
        providerLogoComponent={
          <ProviderLogoComponent
            hotelId={hotelId}
            offer={offer}
            variant={variant}
            isAppLockedDeal={isAppRequired}
            sourceComponent={sourceComponent}
            hasVioTag={isVioTagVisible}
          />
        }
        roomLabelsComponent={
          <RoomLabelsComponent
            hasRoomLabels={hasRoomLabels}
            offer={offer}
            hotelId={hotelId}
            hasVioTag={isVioTagVisible}
          />
        }
        cashbackComponent={
          showCashback && cashbackType && cashbackValue ? (
            <Cashback value={cashbackValue} type={cashbackType} />
          ) : null
        }
        showCashbackLineBreak={showCashbackLineBreak}
        priceComponent={
          <PriceComponent
            hotelId={hotelId}
            offer={offer}
            showAnchorPrice={isVioTagVisible}
            isPrivateDeal={isVioTagVisible || isSpecial}
            anchorPrice={anchorPrice}
            anchorPriceTotal={anchorPriceTotal}
            variant={variant}
            showAppIcon={showArrowCTA}
            isAppLockedDeal={isAppRequired}
          />
        }
        ctaComponent={
          <CtaComponent
            hotelId={hotelId}
            showArrowCTA={showArrowCTA}
            offer={offer}
            disabled={disabled}
          />
        }
      />
    </OfferInteraction>
  )
}
